import { Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import LibraryBookForm from '../../../../components/library/report/book_edited/LibraryBookForm';
import LibraryBookList from '../../../../components/library/report/book_edited/LibraryBookList';
import AppURL from '../../../../widgets/AppURL';
import { fetchDataFromApi, postDataToTheServer } from '../../../../widgets/api';
import { ToastSuccess, ToastError } from '../../../../widgets/ToastMessage';
import { useDispatch } from 'react-redux';
import { pageTitle } from '../../../../store/softSlice';

// ---------------------------------------------------------------------------
// Inline BulkEditModal — replaces the commented-out external import.
// Accepts: isOpen, onClose, onSave(updateData), selectedCount, optionList
// ---------------------------------------------------------------------------
const BulkEditModal = ({ isOpen, onClose, onSave, selectedCount, optionList }) => {
  const [fields, setFields] = useState({
    life_percent: '',
    actual_price: '',
    edition: '',
    remarks: '',
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = () => {
    // Only send non-empty fields so we don't overwrite unintentionally
    const payload = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => v !== '' && v !== null)
    );
    if (Object.keys(payload).length === 0) {
      alert('Please fill at least one field to update.');
      return;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Bulk Edit Books</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Updating {selectedCount} selected book{selectedCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2">
            Only filled fields will be updated. Leave blank to keep existing values.
          </p>
          {[
            { name: 'life_percent', label: 'Life Percent', type: 'number' },
            { name: 'actual_price', label: 'Actual Price', type: 'number' },
            { name: 'edition', label: 'Edition', type: 'number' },
            { name: 'remarks', label: 'Remarks', type: 'text' },
          ].map(({ name, label, type }) => (
            <div key={name}>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
              </label>
              <input
                name={name}
                type={type}
                value={fields[name]}
                onChange={handleChange}
                placeholder={`Enter new ${label.toLowerCase()}…`}
                className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-900/30">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors shadow-sm"
          >
            Update {selectedCount} Book{selectedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------
const BookEditedListFormView = ({ setIconsButton, PageTitle }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(pageTitle({ title: PageTitle }));
    document.title = PageTitle;
    setIconsButton(null);
  }, []);

  const [optionList, setOptionList] = useState([]);
  const [bookEditedList, setBookEditedList] = useState([]);
  const [editedDetails, setEditedDetails] = useState(null); // For single book edit
  const [selectedBookIds, setSelectedBookIds] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchData = async () => {
    try {
      const res = await fetchDataFromApi(AppURL.LibraryURL + 'library-book-options-list-view/');
      setOptionList(res);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBookEditedList = async (urls) => {
    try {
      await fetchDataFromApi(urls).then((res) => {
        setBookEditedList(res);
      })
    } catch (error) {
      console.log(error);
    }
  }
  const FetchData = async () => {
    await fetchBookEditedList(AppURL.LibraryURL + 'library-book-edited-list-view/');
  }
  useEffect(() => {
    FetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const editBook = async (bookId) => {
    try {
      await fetchDataFromApi(AppURL.LibraryURL + `library-book-retrieve-update-view/${bookId}`).then((res) => {
        setEditedDetails(res);
      });
    } catch (error) {
      console.log(error);
    }
  }
  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={4} sm={12} xs={12} md={4} xl={4}>
          <LibraryBookForm
            editedDetails={editedDetails}
            optionList={optionList}
            selectedBookIds={selectedBookIds}
            editBook={editBook}
            BookAddOptionListView={FetchData}
          />
        </Grid>
        <Grid item lg={8} sm={12} xs={12} md={8} xl={8}>
          <LibraryBookList
            bookEditedList={bookEditedList}
            selectedBookIds={selectedBookIds}
            onSelectBooks={setSelectedBookIds}
            editBook={editBook}
            setEditedDetails={setEditedDetails}
            BookAddOptionListView={fetchBookEditedList}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default BookEditedListFormView;